import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  Users,
  FileText,
  FolderOpen,
  HelpCircle,
  BookOpen,
  Trophy,
  Shield,
  Star,
  CheckCircle,
  XCircle,
  Building2
} from 'lucide-react'

interface CommunityDetail {
  id: number
  slug: string
  name: string
  description: string | null
  active: boolean
  settings: Record<string, unknown>
  specialties: string[]
  created_at: string
  updated_at: string
}

interface Member {
  id: number
  name: string
  email: string
  specialty: string
  role: string
  total_points: number
  created_at: string
}

interface TopMember {
  id: number
  name: string
  specialty: string
  total_points: number
  level: number
}

interface RecentPost {
  id: number
  title: string
  post_type: string
  author_name: string
  category_name: string | null
  likes_count: number
  comments_count: number
  created_at: string
}

interface Stats {
  members_count: number
  posts_count: number
  categories_count: number
  faq_items_count: number
  articles_count: number
  total_points: number
  admins_count: number
  moderators_count: number
}

interface ShowProps {
  community: CommunityDetail
  recent_members: Member[]
  top_members: TopMember[]
  recent_posts: RecentPost[]
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function getRoleBadge(role: string) {
  switch (role) {
    case 'admin':
      return { label: 'Admin', className: 'bg-purple-100 text-purple-800', icon: Shield }
    case 'moderator':
      return { label: 'Modérateur', className: 'bg-blue-100 text-blue-800', icon: Star }
    default:
      return { label: 'Membre', className: 'bg-gray-100 text-gray-800', icon: Users }
  }
}

export default function Show({ community, recent_members, top_members, recent_posts, stats }: ShowProps) {
  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer la communauté "${community.name}" ?`)) {
      router.delete(`/admin/communities/${community.slug}`)
    }
  }

  return (
    <AdminLayout
      title={community.name}
      breadcrumbs={[
        { label: 'Communautés', href: '/admin/communities' },
        { label: community.name }
      ]}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Community Info */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-tessan-cream rounded-lg flex items-center justify-center">
                  <Building2 size={24} className="text-tessan-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-text-dark">{community.name}</h2>
                  <p className="text-sm text-text-gray">/{community.slug}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                community.active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {community.active ? (
                  <>
                    <CheckCircle size={14} />
                    Active
                  </>
                ) : (
                  <>
                    <XCircle size={14} />
                    Inactive
                  </>
                )}
              </span>
            </div>

            {community.description && (
              <p className="text-text-gray mb-4">{community.description}</p>
            )}

            {community.specialties && community.specialties.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-text-dark mb-2">Spécialités :</p>
                <div className="flex flex-wrap gap-2">
                  {community.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-tessan-cream text-tessan-primary text-sm rounded"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 text-sm text-text-gray pt-4 border-t border-gray-200">
              <span>Créée le {formatDate(community.created_at)}</span>
              <span>•</span>
              <span>Modifiée le {formatDate(community.updated_at)}</span>
            </div>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <Users size={24} className="mx-auto text-tessan-primary mb-2" />
              <p className="text-2xl font-bold text-text-dark">{stats.members_count}</p>
              <p className="text-sm text-text-gray">Membres</p>
            </Card>
            <Card className="p-4 text-center">
              <FileText size={24} className="mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-text-dark">{stats.posts_count}</p>
              <p className="text-sm text-text-gray">Publications</p>
            </Card>
            <Card className="p-4 text-center">
              <FolderOpen size={24} className="mx-auto text-yellow-600 mb-2" />
              <p className="text-2xl font-bold text-text-dark">{stats.categories_count}</p>
              <p className="text-sm text-text-gray">Catégories</p>
            </Card>
            <Card className="p-4 text-center">
              <Trophy size={24} className="mx-auto text-orange-600 mb-2" />
              <p className="text-2xl font-bold text-text-dark">{stats.total_points.toLocaleString()}</p>
              <p className="text-sm text-text-gray">Points totaux</p>
            </Card>
          </div>

          {/* Recent Posts */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Publications récentes</h3>
            {recent_posts.length === 0 ? (
              <p className="text-text-gray text-center py-4">Aucune publication</p>
            ) : (
              <div className="space-y-3">
                {recent_posts.map((post) => (
                  <div
                    key={post.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-dark truncate">{post.title}</p>
                      <p className="text-sm text-text-gray">
                        par {post.author_name}
                        {post.category_name && ` • ${post.category_name}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-text-gray">
                      <span>{post.likes_count} ❤️</span>
                      <span>{post.comments_count} 💬</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wide mb-4">
              Actions
            </h3>
            <div className="space-y-2">
              <Link href={`/admin/communities/${community.slug}/edit`} className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <Edit size={16} />
                  Modifier
                </Button>
              </Link>
              <Button
                variant="secondary"
                className="w-full justify-start gap-2 text-red-600 hover:bg-red-50"
                onClick={handleDelete}
              >
                <Trash2 size={16} />
                Supprimer
              </Button>
            </div>
          </Card>

          {/* Team Stats */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wide mb-4">
              Équipe
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-purple-600" />
                  <span className="text-sm text-text-dark">Administrateurs</span>
                </div>
                <span className="font-medium">{stats.admins_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star size={16} className="text-blue-600" />
                  <span className="text-sm text-text-dark">Modérateurs</span>
                </div>
                <span className="font-medium">{stats.moderators_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-gray-600" />
                  <span className="text-sm text-text-dark">Membres</span>
                </div>
                <span className="font-medium">{stats.members_count - stats.admins_count - stats.moderators_count}</span>
              </div>
            </div>
          </Card>

          {/* Content Stats */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wide mb-4">
              Contenu
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HelpCircle size={16} className="text-green-600" />
                  <span className="text-sm text-text-dark">FAQ</span>
                </div>
                <span className="font-medium">{stats.faq_items_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} className="text-indigo-600" />
                  <span className="text-sm text-text-dark">Articles</span>
                </div>
                <span className="font-medium">{stats.articles_count}</span>
              </div>
            </div>
          </Card>

          {/* Top Members */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wide mb-4">
              Top Membres
            </h3>
            {top_members.length === 0 ? (
              <p className="text-text-gray text-center py-2 text-sm">Aucun membre</p>
            ) : (
              <div className="space-y-3">
                {top_members.slice(0, 5).map((member, index) => (
                  <div key={member.id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-200 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <Link
                        href={`/admin/users/${member.id}`}
                        className="font-medium text-sm text-text-dark hover:text-tessan-primary truncate block"
                      >
                        {member.name}
                      </Link>
                      <p className="text-xs text-text-gray">{member.specialty}</p>
                    </div>
                    <span className="text-sm font-medium text-tessan-primary">
                      {member.total_points.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Members */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wide mb-4">
              Membres récents
            </h3>
            {recent_members.length === 0 ? (
              <p className="text-text-gray text-center py-2 text-sm">Aucun membre</p>
            ) : (
              <div className="space-y-3">
                {recent_members.slice(0, 5).map((member) => {
                  const roleBadge = getRoleBadge(member.role)
                  return (
                    <div key={member.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-tessan-cream rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-tessan-primary">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          href={`/admin/users/${member.id}`}
                          className="font-medium text-sm text-text-dark hover:text-tessan-primary truncate block"
                        >
                          {member.name}
                        </Link>
                        <p className="text-xs text-text-gray">{member.specialty}</p>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${roleBadge.className}`}>
                        {roleBadge.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
